/**
 * Client Admin Panel
 *
 * Self-service panel for fleet users to manage their organization.
 * Auto-scoped to user's organization (no dropdown selector).
 *
 * Phase 1: UI skeleton with dummy data ✅
 * Phase 2: Real data hooks ✅
 * Phase 3: CRUD operations
 */

import React, { useState } from 'react';
import {
  Users,
  Ship,
  AlertTriangle
} from 'lucide-react';

import { usePermissions } from '../../../hooks/usePermissions';
import { useClientMembers } from '../../../hooks/client/useClientMembers';
import { useClientVessels } from '../../../hooks/client/useClientVessels';
import CrewTab from './CrewTab';
import VesselsTab from './VesselsTab';

const ClientAdminPanel = () => {
  const [activeTab, setActiveTab] = useState('crew');
  const { isFleet, currentUser } = usePermissions();

  // Phase 2: Get organization ID and fetch real data
  const organizationId = currentUser?.organizationId;
  const { members } = useClientMembers(organizationId);
  const { vessels } = useClientVessels(organizationId);

  // Tabs configuration
  const tabs = [
    {
      id: 'crew',
      label: 'My Crew',
      icon: Users,
      badge: null
    },
    {
      id: 'vessels',
      label: 'My Vessels',
      icon: Ship,
      badge: null
    }
  ];

  // Access check - CRITICAL: Only fleet users can access
  if (!isFleet) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-red-800 mb-2">
            Access Denied
          </h2>
          <p className="text-red-700">
            The Manage Crew panel is only available to Fleet users.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header - Auto-scoped to user's organization */}
      <div className="bg-gray-900 text-white px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Users className="h-6 w-6" />
              Manage Crew
            </h1>
            <p className="text-slate-300 text-sm mt-1">
              {currentUser?.organizationName || 'Your Organization'} - {members.length} Members · {vessels.length} Vessels
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-slate-200 px-6">
        <div className="flex gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium
                border-b-2 transition-colors relative
                ${activeTab === tab.id
                  ? 'border-cyan-600 text-cyan-600'
                  : 'border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300'
                }
              `}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.badge && (
                <span className="ml-2 px-1.5 py-0.5 text-xs font-bold bg-cyan-600 text-white rounded">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-auto bg-slate-50 p-6">
        {activeTab === 'crew' && <CrewTab />}
        {activeTab === 'vessels' && <VesselsTab />}
      </div>
    </div>
  );
};

export default ClientAdminPanel;
